import { createRouter, createWebHistory } from 'vue-router'

import { useAuth } from '@/stores/auth'

const pages = import.meta.glob('../views/**/*.vue')
const view = (path) => pages[`../views/${path}.vue`]

// meta.title feeds the breadcrumb; meta.admin hides the route from non-admins.
const appRoutes = [
  { path: '', name: 'dashboard', component: view('Dashboard'), meta: { title: 'Dashboard' } },
  { path: 'search', name: 'search', component: view('analytics/Search'), meta: { title: 'Search' } },
  { path: 'reports', name: 'reports', component: view('analytics/Reports'), meta: { title: 'Reports' } },

  { path: 'websites', name: 'websites', component: view('websites/WebsiteList'), meta: { title: 'Websites' } },
  { path: 'websites/new', name: 'website-new', component: view('websites/WebsiteWizard'), meta: { title: 'New website', parent: 'websites' } },
  { path: 'websites/:id', name: 'website', component: view('websites/WebsiteDetail'), props: true, meta: { title: 'Website', parent: 'websites' } },
  { path: 'websites/:id/groups/:gid', name: 'placement-group', component: view('websites/PlacementGroupDetail'), props: true, meta: { title: 'Placement group', parent: 'websites' } },
  { path: 'websites/:id/groups/:gid/data/:did', name: 'placement-data', component: view('websites/PlacementDataDetail'), props: true, meta: { title: 'Data row', parent: 'websites' } },
  { path: 'group-types', name: 'group-types', component: view('websites/GroupTypes'), meta: { title: 'Group types' } },

  { path: 'players', name: 'players', component: view('players/PlayerList'), meta: { title: 'Players', admin: true } },
  { path: 'players/new', name: 'player-new', component: view('players/PlayerNew'), meta: { title: 'New player', admin: true, parent: 'players' } },
  { path: 'players/:id', name: 'player', component: view('players/PlayerDetail'), props: true, meta: { title: 'Player', admin: true, parent: 'players' } },

  { path: 'demand-sources', name: 'demand-sources', component: view('suppliers/SupplierList'), meta: { title: 'Demand sources' } },
  { path: 'demand-sources/new', name: 'demand-source-new', component: view('suppliers/SupplierDetail'), meta: { title: 'New demand source', parent: 'demand-sources' } },
  { path: 'demand-sources/:id', name: 'demand-source', component: view('suppliers/SupplierDetail'), props: true, meta: { title: 'Demand source', parent: 'demand-sources' } },

  { path: 'users', name: 'users', component: view('users/UserList'), meta: { title: 'Users' } },
  { path: 'users/new', name: 'user-new', component: view('users/UserNew'), meta: { title: 'New user', parent: 'users' } },
  { path: 'users/:id', name: 'user', component: view('users/UserDetail'), props: true, meta: { title: 'User', parent: 'users' } },
  { path: 'profile', name: 'profile', component: view('users/MyProfile'), meta: { title: 'Profile' } },

  { path: 'payments', name: 'payments', component: view('payments/WalletList'), meta: { title: 'Payments' } },
  { path: 'payments/:id', name: 'wallet', component: view('payments/WalletDetail'), props: true, meta: { title: 'Wallet', parent: 'payments' } },

  { path: 'whitelabel', name: 'whitelabel', component: view('whitelabel/WhitelabelList'), meta: { title: 'Whitelabel' } },
  { path: 'whitelabel/new', name: 'whitelabel-new', component: view('whitelabel/WhitelabelDetail'), meta: { title: 'New domain', parent: 'whitelabel' } },
  { path: 'whitelabel/:id', name: 'whitelabel-domain', component: view('whitelabel/WhitelabelDetail'), props: true, meta: { title: 'Domain', parent: 'whitelabel' } },

  { path: 'send-notification', name: 'send-notification', component: view('SendNotification'), meta: { title: 'Send notification', admin: true } },

  { path: 'credentials/groups', name: 'permission-groups', component: view('credentials/PermissionGroups'), meta: { title: 'Permission groups', admin: true } },
  { path: 'credentials/api-keys', name: 'api-keys', component: view('credentials/ApiKeys'), meta: { title: 'API keys', admin: true } },

  { path: 'settings/metrics', name: 'metrics', component: view('settings/Metrics'), meta: { title: 'Metrics' } },
  { path: 'settings/apis', name: 'external-apis', component: view('settings/ExternalApis'), meta: { title: 'External APIs' } },

  { path: 'settings/ai', name: 'ai-providers', component: view('settings/AiProviders'), meta: { title: 'AI providers', admin: true } },

  { path: 'scheduler', name: 'scheduler', component: view('Scheduler'), meta: { title: 'Scheduler', admin: true } },

  { path: 'review', name: 'review', component: view('review/ReviewQueue'), meta: { title: 'Review queue', admin: true } },
  { path: 'assistant/:id?', name: 'assistant', component: view('review/Assistant'), props: true, meta: { title: 'Assistant', admin: true } },
  { path: 'action-log', name: 'action-log', component: view('review/ActionLog'), meta: { title: 'Action log', admin: true } },
  { path: ':rest(.*)*', name: 'not-found', component: view('NotFound'), meta: { title: 'Not found' } },
]

const routes = [
  { path: '/login', name: 'login', component: view('auth/Login'), meta: { public: true, title: 'Sign in' } },
  { path: '/forgot', name: 'forgot', component: view('auth/Forgot'), meta: { public: true, title: 'Reset password' } },
  { path: '/reset/:key', name: 'reset', component: view('auth/Reset'), props: true, meta: { public: true, title: 'Choose a password' } },
  // Emailed verification links use the old misspelt path; keep it working.
  { path: '/verify/:key', alias: '/verifiy/:key', name: 'verify', component: view('auth/Verify'), props: true, meta: { public: true, title: 'Verify account' } },
  { path: '/', component: () => import('../layouts/AppShell.vue'), children: appRoutes },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (to, from, saved) => saved || (to.path !== from.path ? { top: 0 } : undefined),
})

router.beforeEach((to) => {
  const auth = useAuth()
  if (to.meta.public) {
    if (auth.isAuthenticated && to.name === 'login') return { name: 'dashboard' }
    return true
  }
  if (!auth.isAuthenticated) return { name: 'login', query: to.fullPath !== '/' ? { next: to.fullPath } : {} }
  if (to.meta.admin && !auth.isAdmin) return { name: 'not-found', params: { rest: to.path.slice(1).split('/') }, query: { denied: '1' } }
  return true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · AdCrew Management` : 'AdCrew Management'
})
